import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  Scale,
  CreditCard,
  Ban,
  RefreshCw,
  Mail,
  Shield,
  Users,
  Gavel,
  Clock,
  Globe,
  Lock,
  MessageSquare
} from 'lucide-react';

export const Terms = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="terms-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-500" />
              </div>
              <span className="overline">Legal</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl">
              Please read these Terms of Service carefully before using our website or services. 
              By accessing or using our services, you agree to be bound by these terms.
            </p>
            <p className="text-zinc-600 text-sm mt-6">
              Effective Date: January 1, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold mb-2">Important Legal Notice</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  PLEASE READ THESE TERMS OF SERVICE CAREFULLY. BY ACCESSING OR USING OUR SERVICES, YOU ACKNOWLEDGE THAT 
                  YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, 
                  DO NOT ACCESS OR USE OUR SERVICES. THESE TERMS CONTAIN A BINDING ARBITRATION CLAUSE AND CLASS ACTION WAIVER.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl p-8 border border-emerald-500/20"
          >
            <h2 className="text-xl font-bold text-white mb-4 font-['Outfit']">Summary of Key Terms</h2>
            <p className="text-zinc-500 text-sm mb-4">While you should read the full terms below, here are the key points:</p>
            <ul className="space-y-3 text-zinc-400 text-sm">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>You must be at least 18 years old and provide accurate information</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>Our health screenings supplement but do not replace professional medical advice</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>Your health information is protected under HIPAA regulations</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>Disputes will be resolved through binding arbitration in Nevada</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>We may modify these terms with notice to you</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-zinc-900/30" data-testid="terms-sections">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="space-y-8">
            {/* Section 1: Acceptance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">1. Acceptance of Terms</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>
                  These Terms of Service ("Terms") constitute a legally binding agreement between you ("you," "your," or "User") 
                  and HealthCheck Systems, Inc. ("HealthCheck Systems," "Company," "we," "us," or "our") governing your access 
                  to and use of the healthchecksolutions.online website, mobile applications, and all related services 
                  (collectively, the "Services").
                </p>
                <p>
                  By accessing or using our Services, creating an account, or clicking "I agree" to these Terms, you represent 
                  that you have read, understood, and agree to be bound by these Terms and our Privacy Policy, which is 
                  incorporated herein by reference. If you are accessing or using our Services on behalf of a company, organization, 
                  or other entity, you represent and warrant that you have the authority to bind such entity to these Terms.
                </p>
                <p>
                  If you do not agree to these Terms, you must not access or use our Services.
                </p>
              </div>
            </motion.div>

            {/* Section 2: Eligibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">2. Eligibility</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>To use our Services, you must:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Be at least 18 years of age (or the age of majority in your jurisdiction)</li>
                  <li>Have the legal capacity to enter into a binding agreement</li>
                  <li>Not be prohibited from using the Services under applicable laws</li>
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain and promptly update your account information</li>
                </ul>
                <p>
                  For individuals under 18 who require health screening services, a parent or legal guardian must consent 
                  to these Terms and assume responsibility for the minor's use of the Services.
                </p>
              </div>
            </motion.div>

            {/* Section 3: Description of Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">3. Description of Services</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>HealthCheck Systems provides the following services:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Mobile Health Screening:</strong> On-site preventative health screening services delivered via mobile health units to workplaces and other locations</li>
                  <li><strong className="text-white">Healthcare Provider Management:</strong> Coordination and management of healthcare provider networks nationwide</li>
                  <li><strong className="text-white">Corporate Wellness Programs:</strong> Design and implementation of comprehensive workplace wellness initiatives</li>
                  <li><strong className="text-white">Health Data Analytics:</strong> Collection, analysis, and reporting of health screening data</li>
                  <li><strong className="text-white">Compliance Services:</strong> HIPAA compliance support and regulatory reporting</li>
                </ul>
                <p>
                  Our Services are intended for use by businesses, organizations, and their employees for preventative health 
                  screening purposes. Individual consumers should consult their personal healthcare providers for medical advice 
                  and treatment.
                </p>
              </div>
            </motion.div>

            {/* Section 4: Medical Disclaimer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8 border-l-2 border-l-amber-500"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">4. Medical Disclaimer</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p className="text-amber-400 font-medium">
                  IMPORTANT: OUR SERVICES ARE FOR INFORMATIONAL AND PREVENTATIVE SCREENING PURPOSES ONLY AND ARE NOT 
                  INTENDED TO BE A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT.
                </p>
                <p>
                  Health screening results and recommendations provided through our Services are designed to supplement, 
                  not replace, the relationship between you and your healthcare provider. Always seek the advice of your 
                  physician or other qualified health provider with any questions you may have regarding a medical condition 
                  or health concerns.
                </p>
                <p>
                  Never disregard professional medical advice or delay in seeking it because of something you have read 
                  or received through our Services. If you think you may have a medical emergency, call your doctor, 
                  go to the emergency department, or call 911 immediately.
                </p>
                <p>
                  HealthCheck Systems does not recommend or endorse any specific tests, physicians, products, procedures, 
                  opinions, or other information that may be mentioned through our Services.
                </p>
              </div>
            </motion.div>

            {/* Section 5: User Responsibilities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">5. User Responsibilities</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>By using our Services, you agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate, complete, and current information when using our Services</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                  <li>Use our Services only for lawful purposes and in accordance with these Terms</li>
                  <li>Comply with all applicable laws, regulations, and professional standards</li>
                  <li>Respect the privacy and rights of others</li>
                  <li>Cooperate with our screening personnel and follow all safety protocols</li>
                  <li>Disclose relevant health information necessary for safe and accurate screening</li>
                </ul>
              </div>
            </motion.div>

            {/* Section 6: Prohibited Conduct */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Ban className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">6. Prohibited Conduct</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use our Services for any unlawful purpose or in violation of any applicable laws</li>
                  <li>Impersonate any person or entity or falsely state or misrepresent your affiliation</li>
                  <li>Interfere with or disrupt the integrity or performance of our Services</li>
                  <li>Attempt to gain unauthorized access to our systems, networks, or data</li>
                  <li>Transmit any viruses, malware, or other harmful code</li>
                  <li>Collect or harvest any information from our Services without authorization</li>
                  <li>Use automated systems (bots, scrapers) to access our Services</li>
                  <li>Reverse engineer, decompile, or disassemble any part of our Services</li>
                  <li>Remove, alter, or obscure any proprietary notices</li>
                  <li>Use our Services to harass, abuse, or harm another person</li>
                  <li>Engage in any activity that could damage our reputation or business</li>
                  <li>Assist or enable any third party in doing any of the foregoing</li>
                </ul>
              </div>
            </motion.div>

            {/* Section 7: Payment Terms */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">7. Payment Terms</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p><strong className="text-white">7.1 Fees and Pricing</strong></p>
                <p>
                  Fees for our Services are quoted in U.S. dollars and are subject to change with reasonable notice. 
                  Current pricing will be provided upon request or as specified in your service agreement.
                </p>
                
                <p><strong className="text-white">7.2 Payment Terms</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Payment is due within thirty (30) days of invoice date unless otherwise agreed in writing</li>
                  <li>Late payments may incur interest at 1.5% per month (18% annually) or the maximum rate permitted by law</li>
                  <li>You are responsible for all applicable taxes, duties, and government assessments</li>
                  <li>We accept major credit cards, ACH transfers, and wire transfers</li>
                </ul>

                <p><strong className="text-white">7.3 Refunds</strong></p>
                <p>
                  Fees are generally non-refundable. Refund requests will be considered on a case-by-case basis at our 
                  sole discretion. Cancellations made less than 48 hours before scheduled services may be subject to 
                  cancellation fees.
                </p>
              </div>
            </motion.div>

            {/* Section 8: Intellectual Property */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">8. Intellectual Property</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>
                  All content, features, and functionality of our Services, including but not limited to text, graphics, 
                  logos, icons, images, audio clips, digital downloads, data compilations, software, and the compilation 
                  thereof, are the exclusive property of HealthCheck Systems or its licensors and are protected by United 
                  States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>
                <p>
                  "HealthCheck Systems" and our logo are trademarks of HealthCheck Systems, Inc. You may not use our 
                  trademarks without our prior written consent.
                </p>
                <p>
                  We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services 
                  for their intended purposes, subject to these Terms.
                </p>
              </div>
            </motion.div>

            {/* Section 9: Disclaimer of Warranties */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">9. Disclaimer of Warranties</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p className="uppercase">
                  OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
                  OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
                  PURPOSE, TITLE, AND NON-INFRINGEMENT.
                </p>
                <p>
                  WE DO NOT WARRANT THAT: (A) OUR SERVICES WILL MEET YOUR REQUIREMENTS; (B) OUR SERVICES WILL BE 
                  UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (C) THE RESULTS OBTAINED FROM USE OF OUR SERVICES 
                  WILL BE ACCURATE OR RELIABLE; (D) THE QUALITY OF ANY SERVICES OBTAINED WILL MEET YOUR EXPECTATIONS; 
                  OR (E) ANY ERRORS IN OUR SERVICES WILL BE CORRECTED.
                </p>
                <p>
                  SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES, SO THE ABOVE EXCLUSION MAY NOT 
                  APPLY TO YOU.
                </p>
              </div>
            </motion.div>

            {/* Section 10: Limitation of Liability */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">10. Limitation of Liability</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p className="uppercase">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL HEALTHCHECK SYSTEMS, ITS OFFICERS, 
                  DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, SUCCESSORS, OR ASSIGNS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, 
                  GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 uppercase">
                  <li>YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE OUR SERVICES</li>
                  <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON OUR SERVICES</li>
                  <li>ANY CONTENT OBTAINED FROM OUR SERVICES</li>
                  <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT</li>
                </ul>
                <p className="uppercase">
                  OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR OUR SERVICES 
                  SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE 
                  CLAIM; OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
              </div>
            </motion.div>

            {/* Section 11: Indemnification */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">11. Indemnification</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed">
                <p>
                  You agree to defend, indemnify, and hold harmless HealthCheck Systems and its officers, directors, 
                  employees, agents, affiliates, successors, and assigns from and against any and all claims, damages, 
                  obligations, losses, liabilities, costs, and expenses (including but not limited to attorney's fees) 
                  arising from: (a) your use of our Services; (b) your violation of these Terms; (c) your violation of 
                  any third-party right, including without limitation any copyright, trademark, property, or privacy right; 
                  or (d) any claim that your use of our Services caused damage to a third party.
                </p>
              </div>
            </motion.div>

            {/* Section 12: Dispute Resolution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8 border-l-2 border-l-emerald-500"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Gavel className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">12. Dispute Resolution & Arbitration</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p><strong className="text-white">12.1 Informal Resolution</strong></p>
                <p>
                  Before initiating any formal dispute resolution proceeding, you agree to first contact us at 
                  legal@healthchecksolutions.online to attempt to resolve any dispute informally. We will attempt 
                  to resolve the dispute within sixty (60) days of receiving your notice.
                </p>
                
                <p><strong className="text-white">12.2 Binding Arbitration</strong></p>
                <p>
                  If we cannot resolve a dispute informally, you and HealthCheck Systems agree that any dispute, claim, 
                  or controversy arising out of or relating to these Terms or our Services shall be resolved exclusively 
                  through final and binding arbitration, rather than in court. The arbitration will be conducted by the 
                  American Arbitration Association ("AAA") under its Commercial Arbitration Rules.
                </p>

                <p><strong className="text-white">12.3 Class Action Waiver</strong></p>
                <p className="text-amber-400">
                  YOU AND HEALTHCHECK SYSTEMS AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS 
                  INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE 
                  PROCEEDING.
                </p>

                <p><strong className="text-white">12.4 Governing Law</strong></p>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the State of Nevada, 
                  without regard to its conflict of law provisions. The arbitration shall take place in Dallas County, Texas.
                </p>
              </div>
            </motion.div>

            {/* Section 13: Modifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">13. Modifications to Terms</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>
                  We reserve the right to modify these Terms at any time. When we make changes, we will update the 
                  "Effective Date" at the top of these Terms and notify you through:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Email notification to your registered email address</li>
                  <li>Prominent notice on our website</li>
                  <li>In-app notification (if applicable)</li>
                </ul>
                <p>
                  Your continued use of our Services after the effective date of the revised Terms constitutes your 
                  acceptance of the changes. If you do not agree to the revised Terms, you must stop using our Services.
                </p>
              </div>
            </motion.div>

            {/* Section 14: Termination */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">14. Termination</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p>
                  We may terminate or suspend your access to our Services immediately, without prior notice or liability, 
                  for any reason, including without limitation if you breach these Terms. Upon termination, your right to 
                  use our Services will immediately cease.
                </p>
                <p>
                  Provisions of these Terms that by their nature should survive termination shall survive, including but 
                  not limited to intellectual property provisions, warranty disclaimers, indemnification, and limitations 
                  of liability.
                </p>
              </div>
            </motion.div>

            {/* Section 15: General Provisions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-surface rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white font-['Outfit']">15. General Provisions</h2>
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4">
                <p><strong className="text-white">Entire Agreement:</strong> These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference, constitute the entire agreement between you and HealthCheck Systems.</p>
                
                <p><strong className="text-white">Severability:</strong> If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.</p>
                
                <p><strong className="text-white">Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.</p>
                
                <p><strong className="text-white">Assignment:</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.</p>
                
                <p><strong className="text-white">Force Majeure:</strong> We shall not be liable for any failure to perform our obligations where such failure results from circumstances beyond our reasonable control.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16" data-testid="terms-contact">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-surface rounded-2xl p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Outfit']">Contact Information</h2>
            </div>
            <p className="text-zinc-400 mb-6 text-sm">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="text-zinc-500 text-sm space-y-1">
              <p><strong className="text-white">HealthCheck Systems, Inc.</strong></p>
              <p>Legal Department</p>
              <p>901 S Central Expy Ste 100</p>
              <p>Richardson, TX 75080</p>
              <p>Email: legal@healthchecksolutions.online</p>
              <p>Phone: (855) 831-2075</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/privacy" className="text-emerald-500 hover:text-emerald-400 text-sm transition-colors">
              Privacy Policy →
            </Link>
            <Link to="/hipaa" className="text-emerald-500 hover:text-emerald-400 text-sm transition-colors">
              HIPAA Notice →
            </Link>
            <Link to="/contact" className="text-emerald-500 hover:text-emerald-400 text-sm transition-colors">
              Contact Us →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
